import { useSignIn } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Eye, EyeOff, Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { FormInput } from '@/components/global/form-inputs/FormInput';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { VALIDATION_MESSAGES } from '@/constants/validation';
import { toast } from '@/hooks/ui/use-toast';

const resetPasswordFormSchema = z
  .object({
    reset_code: z.string().min(1, {
      message: 'Please enter the code',
    }),
    new_password: z
      .string({
        required_error: VALIDATION_MESSAGES.NEW_PASSWORD_REQUIRED,
      })
      .min(8, {
        message: VALIDATION_MESSAGES.NEW_PASSWORD_TOO_SHORT,
      })
      .regex(/[A-Z]/, {
        message: VALIDATION_MESSAGES.PASSWORD_UPPERCASE_REQUIRED,
      })
      .regex(/[a-z]/, {
        message: VALIDATION_MESSAGES.PASSWORD_LOWERCASE_REQUIRED,
      })
      .regex(/[0-9]/, {
        message: VALIDATION_MESSAGES.PASSWORD_NUMBER_REQUIRED,
      })
      .regex(/[^A-Za-z0-9]/, {
        message: VALIDATION_MESSAGES.PASSWORD_SPECIAL_CHAR_REQUIRED,
      }),
    confirm_password: z.string({
      required_error: VALIDATION_MESSAGES.CONFIRM_PASSWORD_REQUIRED,
    }),
  })
  .refine((data) => data.new_password === data.confirm_password, {
    message: VALIDATION_MESSAGES.PASSWORDS_MUST_MATCH,
    path: ['confirm_password'],
  });

export const ResetPasswordForm = () => {
  // state
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // form
  const resetForm = useForm<z.infer<typeof resetPasswordFormSchema>>({
    resolver: zodResolver(resetPasswordFormSchema),
    defaultValues: {
      new_password: '',
      reset_code: '',
    },
  });

  // custom hooks
  const navigate = useNavigate();
  const { signIn, setActive } = useSignIn();

  const newPassword = resetForm.watch('new_password');
  const confirmPassword = resetForm.watch('confirm_password');

  const passwordRequirements = {
    minLength: newPassword?.length >= 8,
    hasUppercase: /[A-Z]/.test(newPassword || ''),
    hasLowercase: /[a-z]/.test(newPassword || ''),
    hasNumber: /[0-9]/.test(newPassword || ''),
    hasSpecialChar: /[^A-Za-z0-9]/.test(newPassword || ''),
    passwordsMatch: newPassword === confirmPassword && newPassword?.length > 0,
  };

  const allRequirementsMet =
    passwordRequirements.minLength &&
    passwordRequirements.hasUppercase &&
    passwordRequirements.hasLowercase &&
    passwordRequirements.hasNumber &&
    passwordRequirements.hasSpecialChar &&
    passwordRequirements.passwordsMatch;

  // event handlers
  const resetFormSubmit = async (
    values: z.infer<typeof resetPasswordFormSchema>
  ) => {
    setLoading(true);
    const { reset_code, new_password } = values;
    await signIn
      ?.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: reset_code,
        password: new_password,
      })
      .then((result) => {
        if (result.status === 'complete') {
          setActive({ session: result.createdSessionId }).then(() => {
            navigate('/dashboard');
            toast({
              title: 'Password is reset successfully!',
            });
          });
        }
      })
      .catch((err) => {
        console.error('error', err.errors[0].longMessage);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `${err.errors[0].longMessage}`,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const RequirementItem = ({ met, text }: { met: boolean; text: string }) => (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <X className="h-4 w-4 text-red-500" />
      )}
      <span className={met ? 'text-green-600' : 'text-gray-600'}>{text}</span>
    </div>
  );

  return (
    <Form {...resetForm}>
      <form
        onSubmit={resetForm.handleSubmit(resetFormSubmit)}
        className="w-full space-y-4"
      >
        <FormInput
          control={resetForm.control}
          name="reset_code"
          type="text"
          placeholder="Please enter the reset code"
        />

        <div className="relative">
          <FormInput
            control={resetForm.control}
            name="new_password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Please enter the new password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-2 text-gray-500 hover:text-gray-700"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        <div className="relative">
          <FormInput
            control={resetForm.control}
            name="confirm_password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-2 text-gray-500 hover:text-gray-700"
            tabIndex={-1}
          >
            {showConfirmPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {newPassword && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              Password Requirements:
            </p>
            <RequirementItem
              met={passwordRequirements.minLength}
              text="At least 8 characters"
            />
            <RequirementItem
              met={passwordRequirements.hasUppercase}
              text="At least one uppercase letter"
            />
            <RequirementItem
              met={passwordRequirements.hasLowercase}
              text="At least one lowercase letter"
            />
            <RequirementItem
              met={passwordRequirements.hasNumber}
              text="At least one number"
            />
            <RequirementItem
              met={passwordRequirements.hasSpecialChar}
              text="At least one special character"
            />
          </div>
        )}

        {confirmPassword && (
          <RequirementItem
            met={passwordRequirements.passwordsMatch}
            text="Passwords match"
          />
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={loading || !allRequirementsMet}
        >
          {loading ? <Loader2 className="size-4 animate-spin" /> : 'Reset'}
        </Button>
      </form>
    </Form>
  );
};
